import {ApolloCache, ApolloLink, gql} from '@apollo/client/core';
import {DocumentNode, FieldNode, Kind, OperationTypeNode, SelectionSetNode, visit} from 'graphql';
import {defer, Observable, timer} from 'rxjs';
import {mergeMap, tap} from 'rxjs/operators';

/** Which process field of a PO item a mutation starts, keyed by the mutation's root field. */
const STARTS: {[field: string]: ProcessField} = {
  restartPoItem: 'workflow_process',
  publishPoItem: 'publish_process',
};
const PROCESS_FIELDS = ['workflow_process', 'publish_process'] as const;
type ProcessField = typeof PROCESS_FIELDS[number];

const PROCESSES = gql`fragment VTK3094CachedProcesses on POItem {
  workflow_process { id start_date }
  publish_process { id start_date }
}`;

/**
 * VTK-3094: a poll must not move a PO item back to an older run. Restart and Publish answer with the new
 * process at once, but the backend points the item at it only seconds later, when the started workflow's first
 * step runs; a poll answered in between still carries the previous run (or no publication yet) with all of that
 * run's fields -- tracks, vtk_jobs, checkpoints, format_specific_data.
 *
 * The decision is per item, never per field: an item in a query answer is stale when, compared with the cache,
 * one of its processes is a different process that started earlier, or when it does not yet show a process a
 * Restart/Publish in this tab started (until that shows, or `giveUpMs` have passed, so a run that never got
 * going cannot freeze the row). A stale item is replaced in the answer by the cache's own copy of it, so that
 * item stays exactly as it is while every other item of the answer is applied. If the cache cannot supply the
 * item in the query's shape, the whole answer is held back and the query sent again after `retryMs`, for at most
 * `giveUpMs`; then the answer is delivered as it is.
 *
 * Once an answer showing the started process is delivered, answers to queries sent before that one are still
 * held to it -- two overlapping polls can answer out of order -- while later queries are trusted.
 */
export function awaitProcessSwitchLink(cache: () => ApolloCache, retryMs = 2000, giveUpMs = 60000,
                                       now = () => Date.now()): ApolloLink {
  // metBy: the send number of the first query whose delivered answer showed the started process. Answers to
  // queries sent before it may still read the item from before the switch; answers to later ones are trusted.
  const started = new Map<string, {field: ProcessField, id: string, at: number, metBy?: number}>();
  let sends = 0;
  const itemKey = (poName: string, poItemId: string) => `${poName}\u0000${poItemId}`;
  const startOf = (p: any) => p?.start_date ? Date.parse(p.start_date) : NaN;

  const isStale = (item: any, key: string, sent: number): boolean => {
    const expected = started.get(key);
    if (expected && expected.field in item && item[expected.field]?.id !== expected.id
      && (expected.metBy === undefined ? now() - expected.at < giveUpMs : sent < expected.metBy)) {
      return true;
    }
    const id = cache().identify(item);
    const cached: any = id ? cache().readFragment({id, fragment: PROCESSES, returnPartialData: true}) : null;
    return PROCESS_FIELDS.some(field => {
      const mine = cached?.[field], theirs = item[field];
      // A process only our own mutation put into the cache, which no answer has shown yet, is not evidence: the
      // expectation above decides about it, and once that has expired unconfirmed the server's item wins.
      if (expected?.field === field && expected.metBy === undefined && mine?.id === expected.id) return false;
      return field in item && mine && theirs && mine.id !== theirs.id && startOf(theirs) < startOf(mine);
    });
  };

  const settle = (answer: ApolloLink.Result, poitemsSelection: SelectionSetNode | null, sent: number): ApolloLink.Result | null => {
    const pos = (answer.data as any)?.list_POs?.pos;
    if (!Array.isArray(pos)) return answer;
    const delivered: [string, any][] = [];
    let held = false;
    const newPos = pos.map((po: any) => !Array.isArray(po?.poitems) ? po : {...po, poitems: po.poitems.map((item: any) => {
      const key = itemKey(po.po_name, item?.po_item_id);
      if (!item || !isStale(item, key, sent)) {
        delivered.push([key, item]);
        return item;
      }
      const id = cache().identify(item);
      const fragment = poitemsSelection && fragmentFor(poitemsSelection);
      const kept = id && fragment ? cache().readFragment({id, fragment}) : null;
      if (!kept) held = true;
      return kept;
    })});
    if (held) return null;
    for (const [key, item] of delivered) {
      const expected = started.get(key);
      if (expected && expected.metBy === undefined && item?.[expected.field]?.id === expected.id) expected.metBy = sent;
    }
    return {...answer, data: {...answer.data, list_POs: {...(answer.data as any).list_POs, pos: newPos}}};
  };

  return new ApolloLink((operation, forward) => {
    if (operation.operationType === OperationTypeNode.MUTATION) {
      return forward(operation).pipe(tap(result => {
        const {po_name, po_item_id} = operation.variables as any;
        for (const [root, field] of Object.entries(STARTS)) {
          const id = (result.data as any)?.[root]?.id;
          if (id && po_name && po_item_id) started.set(itemKey(po_name, po_item_id), {field, id, at: now()});
        }
      }));
    }
    if (operation.operationType !== OperationTypeNode.QUERY) {
      return forward(operation);
    }
    const poitemsSelection = selectionOf(operation.query, 'poitems');
    const firstSent = now();
    const send = (): Observable<ApolloLink.Result> => defer(() => {
      const sent = ++sends;
      return forward(operation).pipe(mergeMap(answer => {
        const settled = settle(answer, poitemsSelection, sent);
        if (settled) return [settled];
        // Held back for giveUpMs already: deliver what the backend says rather than keep the query waiting.
        return now() - firstSent >= giveUpMs ? [answer] : timer(retryMs).pipe(mergeMap(send));
      }));
    });
    return send();
  });
}

/** The selection set of the first field called `name` in a query. */
function selectionOf(query: DocumentNode, name: string): SelectionSetNode | null {
  let found: SelectionSetNode | null = null;
  visit(query, {Field(node: FieldNode) { if (!found && node.name.value === name && node.selectionSet) found = node.selectionSet; }});
  return found;
}

const fragments = new WeakMap<SelectionSetNode, DocumentNode>();

/** A fragment on POItem with the query's own selection for a PO item, to read that item back from the cache. */
function fragmentFor(selectionSet: SelectionSetNode): DocumentNode {
  let fragment = fragments.get(selectionSet);
  if (!fragment) {
    fragment = {kind: Kind.DOCUMENT, definitions: [{
      kind: Kind.FRAGMENT_DEFINITION,
      name: {kind: Kind.NAME, value: 'VTK3094CachedPoItem'},
      typeCondition: {kind: Kind.NAMED_TYPE, name: {kind: Kind.NAME, value: 'POItem'}},
      selectionSet,
    }]};
    fragments.set(selectionSet, fragment);
  }
  return fragment;
}
